/**
 * Activity heartbeat — drives the realm's "online" presence.
 *
 * Presence used to be inferred from any authenticated request, so the app's own
 * 60s background pollers (presence panel, Compline bell) kept a left-open or
 * backgrounded tab "online" 24/7 even with nobody at the keyboard. Now the
 * server only marks a player present when this heartbeat fires, and we send it
 * ONLY while:
 *   • the tab is visible (document.visibilityState === 'visible'), AND
 *   • the player has interacted (pointer / key / scroll / touch) within
 *     IDLE_LIMIT_MS.
 * A hidden or idle tab sends nothing, so it ages out of the 15-minute presence
 * window and correctly drops offline; touching the page brings it back at once.
 *
 * Mounted once from main.ts. Best-effort: a failed beat is ignored.
 */
import { api } from '../api/client.js';
import { store } from '../state/store.js';

const HEARTBEAT_MS = 60_000; // cadence while active (matches the server throttle)
const IDLE_LIMIT_MS = 5 * 60_000; // no interaction this long → treated as away

let lastInteraction = Date.now();
let lastSent = 0;
let started = false;

function markInteraction(): void {
  lastInteraction = Date.now();
}

function isActive(): boolean {
  return (
    !!store.get().accessToken &&
    document.visibilityState === 'visible' &&
    Date.now() - lastInteraction < IDLE_LIMIT_MS
  );
}

/** Send a beat if the player is genuinely active. `force` bypasses the local
 *  cadence throttle (used when returning to the tab) but never the active gate. */
function beat(force = false): void {
  if (!isActive()) return;
  const now = Date.now();
  if (!force && now - lastSent < HEARTBEAT_MS - 5_000) return;
  lastSent = now;
  void api.heartbeat().catch(() => {
    /* presence is best-effort — never surface a heartbeat failure */
  });
}

export function mountPresenceHeartbeat(): void {
  if (started) return;
  started = true;

  const passive: AddEventListenerOptions = { passive: true };
  for (const ev of ['pointerdown', 'pointermove', 'keydown', 'scroll', 'touchstart', 'click']) {
    window.addEventListener(ev, markInteraction, passive);
  }

  // Returning to the tab counts as activity and beats immediately, so a player
  // who tabs back shows online without waiting for the next cadence tick.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      markInteraction();
      beat(true);
    }
  });

  beat(true); // on load, if visible
  // setInterval keeps firing in background tabs (browser-throttled), but isActive()
  // gates each beat to visible + recently-interacted, so a hidden/idle tab is silent.
  window.setInterval(() => beat(false), HEARTBEAT_MS);
}
